import { useEffect, useRef } from 'react';
import type { LaidOutEdge } from '@/components/value-chain/layoutTree';
import { NODE_HEIGHT, NODE_WIDTH, edgePath } from '@/components/value-chain/layoutTree';
import type { ValueChainNode } from '@/types/valueChain';

export const VALUE_CHAIN_MIN_ZOOM = 0.25;
export const VALUE_CHAIN_MAX_ZOOM = 2.5;
const GRAPH_TOP_OFFSET = 80;

type Props = {
  nodes: ValueChainNode[];
  positions: Record<string, { x: number; y: number }>;
  edges: LaidOutEdge[];
  width: number;
  height: number;
  pan: { x: number; y: number };
  zoom: number;
  onSelect: (id: string) => void;
  onPanChange: (pan: { x: number; y: number }) => void;
  onZoomChange: (zoom: number) => void;
};

export function ValueChainCanvas({
  nodes,
  positions,
  edges,
  width,
  height,
  pan,
  zoom,
  onSelect,
  onPanChange,
  onZoomChange,
}: Props) {
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const containerRef = useRef<HTMLDivElement>(null);
  panRef.current = pan;
  zoomRef.current = zoom;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const currentZoom = zoomRef.current;
      const currentPan = panRef.current;
      const intensity = event.deltaMode === 1 ? 0.08 : 0.0016;
      const nextZoom = Math.min(
        VALUE_CHAIN_MAX_ZOOM,
        Math.max(VALUE_CHAIN_MIN_ZOOM, currentZoom * Math.exp(-event.deltaY * intensity)),
      );
      if (Math.abs(nextZoom - currentZoom) < 0.0001) return;

      const scale = nextZoom / currentZoom;
      const cx = event.clientX - el.getBoundingClientRect().left;
      const cy = event.clientY - el.getBoundingClientRect().top;
      const originX = el.clientWidth / 2 + currentPan.x;
      const originY = GRAPH_TOP_OFFSET + currentPan.y;

      onZoomChange(nextZoom);
      onPanChange({
        x: cx - (cx - originX) * scale - el.clientWidth / 2,
        y: cy - (cy - originY) * scale - GRAPH_TOP_OFFSET,
      });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [onPanChange, onZoomChange]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-node-id]')) return;
    event.preventDefault();
    dragging.current = true;
    last.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = event.clientX - last.current.x;
    const dy = event.clientY - last.current.y;
    last.current = { x: event.clientX, y: event.clientY };
    onPanChange({ x: panRef.current.x + dx, y: panRef.current.y + dy });
  };

  const handlePointerUp = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full cursor-grab overflow-hidden bg-[#f8f9fb] select-none active:cursor-grabbing"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <div
        className="absolute left-1/2 top-20 origin-top"
        style={{
          transform: `translate(calc(-50% + ${pan.x}px), ${pan.y}px) scale(${zoom})`,
          width,
          height,
        }}
      >
        <svg
          className="pointer-events-none absolute inset-0"
          width={width}
          height={height}
          fill="none"
        >
          {edges.map((edge) => (
            <g key={edge.id}>
              <path
                d={edgePath(edge.start, edge.end)}
                stroke={edge.supplyChain ? '#93c5fd' : '#d1d5db'}
                strokeWidth={1.5}
                strokeDasharray={edge.supplyChain ? '5 4' : undefined}
              />
              <text
                x={edge.label.x}
                y={edge.label.y - 6}
                textAnchor="middle"
                className="fill-gray-400"
                style={{ fontSize: 10, letterSpacing: '0.08em' }}
              >
                {edge.relation}
              </text>
            </g>
          ))}
        </svg>

        {nodes.map((node) => {
          const pos = positions[node.id];
          if (!pos) return null;
          return (
            <button
              key={node.id}
              type="button"
              data-node-id={node.id}
              onClick={() => onSelect(node.id)}
              className="absolute flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-center select-none outline-none hover:border-gray-400 focus:outline-none focus-visible:outline-none"
              style={{
                left: pos.x,
                top: pos.y,
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
              }}
            >
              <span className="max-w-full truncate text-[13px] font-medium leading-tight text-gray-900">
                {node.name}
              </span>
              <span className="mt-0.5 max-w-full truncate text-[11px] leading-tight text-gray-400">
                {node.type}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
